import type {Observer} from "rxjs";
import {catchError, EMPTY, finalize, mergeMap, Observable, Subject, tap} from "rxjs";
import {action, computed, makeObservable, observable, reaction} from "mobx";
import type {AxiosError} from "axios";
import {isAxiosError} from "axios";

/**
 * Manages a pipe of objects to be processed sequentially
 * T - type of object to process
 * R - type of result of processing
 * K - type of key to identify the object (used to track which objects are being processed)
 */
export class PipeManager<T, R = unknown, K extends string | number = number> {
    private readonly pipe = new Subject<[K, T, Observer<R> | undefined]>()
    readonly inPipe = new Set<K>();
    error = ''

    constructor(process: (o: T) => Observable<R>) {
        makeObservable(this, {
            process: false,
            inPipe: observable,
            error: observable,
            setError: action,
            setInPipe: action,
            hasInPipe: computed,
            nInPipe: computed,
        })
        this.pipe.pipe(
            tap(([key]) => this.setInPipe(key, true)),
            mergeMap(([key, object, observer]) =>
                process(object).pipe(
                    tap(result => {
                        console.log('Processed element', key, object, result, Boolean(observer));
                        if (observer) observer.next(result);
                        this.setError('');
                    }),
                    finalize(() => {
                        this.setInPipe(key, false)
                        if (observer) observer.complete();
                    }),
                    catchError((err) => {
                        console.error('Cannot process element', key, JSON.stringify(object), err);
                        if (isAxiosError(err) || err?.name === 'AxiosError') {
                            const axiosError: AxiosError<any> = err;
                            const msg = axiosError.response?.data?.message
                                || axiosError.response?.data
                                || axiosError.response?.statusText
                                || axiosError.message
                                || String(axiosError)
                            this.setError(msg);
                        } else {
                            this.setError(String(err));
                        }
                        if (observer) observer.error(err);
                        return EMPTY;
                    }),
                ))
        ).subscribe()
    }

    process(key: K, object: T, observer?: Observer<R>) {
        this.pipe.next([key, object, observer]);
    }

    processAndObserve(key: K, object: T): Observable<R> {
        return new Observable<R>(subscriber => {
            this.process(key, object, subscriber);
        });
    }

    processAndPromise(key: K, object: T, beforeReject?: (reason?: any) => void): Promise<R> {
        return new Promise<R>((resolve, reject) => {
            this.process(key, object, {
                next: resolve,
                error: (reason) => {
                    if (beforeReject)
                        beforeReject(reason);
                    reject(reason);
                },
                complete: () => {
                }
            });
        });
    }

    setInPipe(key: K, inPipe: boolean) {
        if (inPipe) {
            this.inPipe.add(key);
        } else {
            this.inPipe.delete(key);
        }
    }

    setError(error: string) {
        this.error = error;
    }

    get hasInPipe() {
        return this.inPipe.size !== 0;
    }

    get nInPipe(): number {
        return this.inPipe.size;
    }
}

export class PipeManager2<T, R = any> {
    private inc = 0;
    public readonly m: PipeManager<T, R>;

    constructor(process: (o: T) => Observable<R>, private onEmpty: Subject<void>) {
        this.m = new PipeManager<T, R>(process);
        reaction(() => this.m.nInPipe, (next, prev) => {
            console.log('PipeManager2', prev, next);
            if (next === 0 && prev > 0) {
                // We went to zero
                onEmpty.next();
            }
        })
    }

    processNew(object: T) {
        this.m.process(this.inc++, object);
    }
}
